import * as React from "react";
import {
  IStackTokens,
  Stack,
  TextField,
  Toggle,
  DefaultButton,
  PrimaryButton,
  Dropdown,
  ITag,
  TagPicker,
  IBasePickerSuggestionsProps,
  Label,
  Text,
} from "office-ui-fabric-react";
import { IAdmin, IDataCenter } from "../../vm";
import CustomDrawer from "../common/CustomDrawer";
import { Formik, FormikErrors } from "formik";
import { EMAIL_REGEX, ADMIN_ROLES } from "../common/Constants";
import {
  updateDataCenterManager,
  addDataCenterManager,
} from "../../services/datacenter/DataCenterManagerService";
import { ToastContext } from "../common/ToastProvider";
import PasswordPolicy from "../common/PasswordPolicy";
import { ADMIN_USER_ROLES, isValidPassword } from "../../services/UtilService";

export interface ManageAdminDialogProps {
  data?: IAdmin;
  closeManageDialog: any;
  handleLoading: any;
  datacenters: ITag[];
}

const ManageAdminDialog: React.FC<ManageAdminDialogProps> = ({
  handleLoading,
  closeManageDialog,
  data,
  datacenters,
}) => {
  const [state, setState] = React.useState({
    admin: {
      email: "",
      name: "",
      password: "",
      isEnabled: true,
      role: undefined,
      datacenterIds: [],
      ...data,
    },
    selectedDatacenters: [],
    isLoading: false,
  } as {
    admin: IAdmin;
    selectedDatacenters: ITag[];
    isLoading: boolean;
  });

  React.useEffect(() => {
    if (data && data.datacenterIds && data.datacenterIds.length > 0) {
      let selectedDatacenters: any = data.datacenterIds.map((d) => {
        return datacenters.find((e) => e.key === d);
      });
      setState({ ...state, selectedDatacenters });
    }
  }, []);

  const closeAddDialog = (data?: IAdmin) => {
    closeManageDialog(data);
  };

  const wrapStackTokens: IStackTokens = { childrenGap: 16 };
  const { showToast } = React.useContext(ToastContext);

  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No datacenter found",
  };

  const filterSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    let allDataCenterList = [...datacenters];

    return filterText
      ? allDataCenterList.filter(
          (tag) =>
            tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
            !listContainsTagList(tag, tagList)
        )
      : [];
  };

  const showAllTags = (selectedItems?: ITag[] | undefined) => {
    let allDataCenterList = [...datacenters];
    return removeDuplicates(allDataCenterList, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={state.admin._id ? "Update Admin" : "Add Admin"}
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={state.admin}
            validate={(values: any) => {
              let errors: FormikErrors<IAdmin> = {};
              if (!values.name) {
                errors.name = "Required";
              }
              if (!values._id && !values.password) {
                errors.password = "Required";
              }
              if (
                !values._id &&
                values.password &&
                !isValidPassword(values.password, ADMIN_USER_ROLES.includes(values.role))
              ) {
                errors.password = "Please enter valid password.";
              }
              if (!values.email) {
                errors.email = "Required";
              }
              if (values.email && !EMAIL_REGEX.test(values.email)) {
                errors.email = "Invalid email address";
              }
              if (!values.role && values.role !== 0) {
                errors.role = "Please select one";
              }
              if (values.role === 3 && values.datacenterIds.length <= 0) {
                errors.datacenterIds = "Please select one";
              }
              return errors;
            }}
            onSubmit={async (values: IAdmin, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              if (state.admin._id) {
                result = await updateDataCenterManager(obj);
              } else {
                result = await addDataCenterManager(obj);
              }
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
                showToast(
                  `Admin ${
                    state.admin._id ? "updated" : "created"
                  } successfully`,
                  "success"
                );
              } else {
                showToast(result?.message ||
                  `Error while ${
                    state.admin._id ? "updating" : "adding"
                  } an Admin`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item className="custom-textfield">
                    <TextField
                      name="name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      label="Name"
                      errorMessage={
                        errors.name && touched.name ? errors.name : ""
                      }
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-textfield">
                    <TextField
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      label="Email"
                      errorMessage={
                        errors.email && touched.email ? errors.email : ""
                      }
                    />
                  </Stack.Item>
                  {state.admin._id === undefined && (
                    <>
                      <Stack.Item className="custom-textfield">
                        <TextField
                          name="password"
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                          label="Password"
                          errorMessage={
                            errors.password && touched.password
                              ? errors.password
                              : ""
                          }
                        />
                      </Stack.Item>
                      {errors.password && touched.password && values.password && (
                        <Stack.Item>
                          <PasswordPolicy password={values.password} isAdmin={ADMIN_USER_ROLES.includes(values.role)} />
                        </Stack.Item>
                      )}
                    </>
                  )}
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={ADMIN_ROLES}
                      //   selectedKey={data?.role ? data.role : undefined}
                      selectedKey={values.role}
                      label="Role"
                      placeholder="Select an option"
                      onChange={(e, item): void => {
                        setFieldValue("role", item?.key);
                      }}
                      errorMessage={
                        errors.role && touched.role ? errors.role : ""
                      }
                      // styles={dropdownStyles}
                    />
                  </Stack.Item>
                  {values.role === 3 && (
                    <Stack.Item className="custom-picker">
                      <Label>Datacenter</Label>
                      <TagPicker
                        removeButtonAriaLabel="Remove"
                        defaultSelectedItems={state.selectedDatacenters}
                        onResolveSuggestions={filterSuggestedTags as any}
                        pickerSuggestionsProps={pickerSuggestionsProps}
                        onEmptyResolveSuggestions={showAllTags}
                        inputProps={{
                          placeholder: "Search a particular datacenter",
                        }}
                        onChange={(items: any) => {
                          let datacenterIds: string[] = [];
                          items.forEach((i: any) => {
                            datacenterIds.push(i.key);
                          });
                          setFieldValue("datacenterIds", datacenterIds);
                        }}
                      />
                      {errors.datacenterIds && touched.datacenterIds && (
                        <Text className="error-msg">
                          {errors.datacenterIds}
                        </Text>
                      )}
                    </Stack.Item>
                  )}
                  <Stack.Item>
                    <Toggle
                      defaultChecked={values.isEnabled}
                      onText="Enabled"
                      offText="Disabled"
                      onChange={(e, v) => {
                        setFieldValue("isEnabled", v);
                      }}
                    />
                  </Stack.Item>
                </Stack>
                <Stack.Item>
                  <Stack
                    horizontal
                    wrap
                    tokens={wrapStackTokens}
                    horizontalAlign="end"
                  >
                    <DefaultButton
                      text="Cancel"
                      onClick={() => closeAddDialog()}
                      className="borderless-btn"
                    />
                    <PrimaryButton
                      type="submit"
                      text={state.admin._id ? "Update" : "Add"}
                    />
                  </Stack>
                </Stack.Item>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageAdminDialog;
